import { useEffect, useState, useCallback } from "react";
import {
  CheckCircle2, XCircle, Wallet, MessageSquare, Hourglass, Info, MailOpen, Megaphone, Sparkles, Gift,
} from "lucide-react";
import { api } from "../lib/api";

const ICONS = {
  approval: { icon: CheckCircle2 },
  rejection: { icon: XCircle },
  cashout: { icon: Wallet },
  balance: { icon: Wallet },
  message: { icon: MessageSquare },
  review: { icon: Hourglass },
  broadcast: { icon: Megaphone },
  info: { icon: Info },
  spin: { icon: Sparkles },
  spin_win: { icon: Sparkles },
  referral: { icon: Gift },
};

export default function Inbox() {
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const data = await api.notifications();
      setItems(data);
      await api.markRead();
    } catch {}
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="page fade-in">
      <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: -1, margin: 0 }}>Inbox</h1>
      <p style={{ color: "var(--text-secondary)", fontSize: 14, marginTop: 6, marginBottom: 22 }}>
        Updates from the team and your account.
      </p>

      {items.length === 0 ? (
        <div data-testid="notif-empty" style={{ textAlign: "center", marginTop: 80, padding: "0 30px" }}>
          <MailOpen size={48} color="var(--text-muted)" style={{ margin: "0 auto" }} />
          <div style={{ fontSize: 16, fontWeight: 700, marginTop: 12 }}>No notifications yet</div>
          <div style={{ color: "var(--text-secondary)", fontSize: 13, marginTop: 6 }}>
            You'll see receipt updates and messages here.
          </div>
        </div>
      ) : (
        items.map((n, idx) => {
          const meta = ICONS[n.kind] || ICONS.info;
          const Icon = meta.icon;
          // Per-notification palette
          //  review        → white card, dark text, orange icon (pending receipt under review)
          //  approval      → solid GREEN (money credited — receipt approved / streak bonus)
          //  referral      → solid GREEN (money credited — referral bonus, both sides)
          //  cashout       → solid GREEN (money paid out to user's bank)
          //  spin_win      → solid green (legacy — pre-Spin-removal)
          //  balance       → solid orange (admin adjustment — could be up or down)
          //  spin          → solid orange (legacy)
          //  rejection     → solid red
          //  info/broadcast/message → alternate pink/orange for rhythm
          const GREEN = { bg: "#1FAE5E", border: "#1FAE5E", text: "#FFFFFF", sub: "rgba(255,255,255,0.92)", iconBg: "rgba(255,255,255,0.22)", iconBorder: "rgba(255,255,255,0.35)", icon: "#FFFFFF" };
          let palette;
          if (n.kind === "review") {
            palette = { bg: "#FFFFFF", border: "var(--border)", text: "var(--navy)", sub: "var(--text-secondary)", iconBg: "var(--peach-tint-soft)", iconBorder: "var(--peach-border)", icon: "var(--primary)" };
          } else if (n.kind === "rejection") {
            palette = { bg: "var(--error)", border: "var(--error)", text: "#FFFFFF", sub: "rgba(255,255,255,0.92)", iconBg: "rgba(255,255,255,0.22)", iconBorder: "rgba(255,255,255,0.35)", icon: "#FFFFFF" };
          } else if (
            n.kind === "approval" ||
            n.kind === "spin_win" ||
            n.kind === "cashout" ||
            n.kind === "referral"
          ) {
            // Anything money-in / money-paid-out → solid green.
            palette = GREEN;
          } else if (n.kind === "spin" || n.kind === "balance") {
            palette = { bg: "var(--primary)", border: "var(--primary)", text: "#FFFFFF", sub: "rgba(255,255,255,0.92)", iconBg: "rgba(255,255,255,0.22)", iconBorder: "rgba(255,255,255,0.35)", icon: "#FFFFFF" };
          } else {
            // info / broadcast / message
            const usePink = idx % 2 === 0;
            const bg = usePink ? "var(--accent-pink)" : "var(--primary)";
            palette = { bg, border: bg, text: "#FFFFFF", sub: "rgba(255,255,255,0.92)", iconBg: "rgba(255,255,255,0.22)", iconBorder: "rgba(255,255,255,0.35)", icon: "#FFFFFF" };
          }
          return (
            <div
              key={n.id}
              data-testid={`notif-${n.id}`}
              style={{
                display: "flex", gap: 14,
                background: palette.bg, border: `1px solid ${palette.border}`,
                borderRadius: 28, padding: 16, marginBottom: 12,
                boxShadow: "0 14px 28px -20px rgba(11,36,71,0.35)",
              }}
            >
              <div style={{
                width: 42, height: 42, borderRadius: 12,
                background: palette.iconBg,
                border: `1px solid ${palette.iconBorder}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <Icon size={20} color={palette.icon} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: palette.text }}>{n.title}</div>
                <div style={{ color: palette.sub, fontSize: 13, marginTop: 4, lineHeight: 1.45 }}>
                  {n.body}
                </div>
                <div style={{ color: palette.sub, fontSize: 11, marginTop: 8, opacity: 0.85 }}>
                  {new Date(n.created_at).toLocaleDateString()}{" "}
                  {new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
